/**
 * API client with token injection for authenticated requests.
 * Platform-agnostic - each platform provides its own getToken implementation.
 */

/**
 * Current API version. All requests are prefixed with /api/v{API_VERSION}.
 * When breaking changes are introduced, a new version will be released.
 */
export const API_VERSION = '1';

/**
 * API path prefix including version.
 * e.g., '/api/v1'
 */
export const API_PREFIX = `/api/v${API_VERSION}`;

export type GetTokenFn = () => Promise<string | null>;

export interface ApiClientConfig {
  baseUrl: string;
  getToken: GetTokenFn;
}

let config: ApiClientConfig | null = null;

/**
 * Current locale for Accept-Language header.
 * Set via setApiLocale() when the user's locale changes.
 */
let currentLocale = 'en';

/**
 * Configure the API client with base URL and token retrieval function.
 * Must be called before making any API requests.
 */
export function configureApiClient(cfg: ApiClientConfig): void {
  config = cfg;
}

/**
 * Get the current API client configuration.
 * @throws Error if configureApiClient hasn't been called
 */
export function getApiClient(): ApiClientConfig {
  if (!config) {
    throw new Error('API client not configured. Call configureApiClient first.');
  }
  return config;
}

/**
 * Set the locale for API requests.
 * The locale is sent via the Accept-Language header for
 * endpoints that return localized content (e.g., announcements).
 *
 * @param locale - The locale to use (e.g., 'en', 'fr', 'es')
 */
export function setApiLocale(locale: string): void {
  currentLocale = locale;
}

/**
 * Get the current API locale.
 *
 * @returns The current locale
 */
export function getApiLocale(): string {
  return currentLocale;
}

/**
 * Custom error class for API errors with status code and response data.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Make an authenticated API request.
 * Automatically injects the Authorization header with the JWT token.
 * All requests are automatically prefixed with the API version path (e.g., /api/v1).
 *
 * @param endpoint - API endpoint path (e.g., '/users/me' becomes '/api/v1/users/me')
 * @param options - Standard fetch options
 * @returns Parsed JSON response
 * @throws ApiError on non-2xx responses
 */
export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { baseUrl, getToken } = getApiClient();
  const token = await getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept-Language': currentLocale,
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${baseUrl}${API_PREFIX}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(
      response.status,
      errorData.message || `Request failed with status ${response.status}`,
      errorData
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Make an authenticated API request with file upload.
 * Uses FormData for multipart/form-data requests.
 * All requests are automatically prefixed with the API version path (e.g., /api/v1).
 *
 * @param endpoint - API endpoint path (e.g., '/users/me/avatar' becomes '/api/v1/users/me/avatar')
 * @param file - File to upload (Blob, File, or Buffer-like object)
 * @param fieldName - Form field name for the file (default: 'file')
 * @returns Parsed JSON response
 * @throws ApiError on non-2xx responses
 */
export async function apiUpload<T>(
  endpoint: string,
  file: Blob | File,
  fieldName = 'file'
): Promise<T> {
  const { baseUrl, getToken } = getApiClient();
  const token = await getToken();

  const formData = new FormData();
  formData.append(fieldName, file);

  // Don't set Content-Type - browser will set it with boundary for multipart/form-data
  const headers: HeadersInit = {
    'Accept-Language': currentLocale,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${baseUrl}${API_PREFIX}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new ApiError(
      response.status,
      errorData.message || `Request failed with status ${response.status}`,
      errorData
    );
  }

  return response.json() as Promise<T>;
}
